import { reactive, watch } from "vue";

export function useCursor({ cursorRef, lastMouseRef, isDarkModeRef, refreshSnapshot }) {
    const state = reactive({
        isCursorActive: false,
        cursorColor: "#111827",
        normalCursorColor: "#111827",
        hoverCursorColor: "#B91C1C",
        cursorScale: 100,
        cursorScaleStep: 20,
        hasCustomCursorColors: false,
        isCursorPointer: false,
        isCursorSettingsMenuOpen: false,
        isApplyingCursorThemeDefaults: false,
    });

    function getCursorThemeDefaults() {
        if (isDarkModeRef.value) {
            return { normal: "#F5F7FA", hover: "#FFD24D" };
        }
        return { normal: "#111827", hover: "#B91C1C" };
    }

    function normalizeHexColor(color) {
        if (typeof color !== "string") return null;
        const value = color.trim().replace("#", "");
        if (!/^[0-9a-fA-F]{3}$/.test(value) && !/^[0-9a-fA-F]{6}$/.test(value)) return null;
        if (value.length === 3) {
            return value
                .split("")
                .map((character) => character + character)
                .join("")
                .toUpperCase();
        }
        return value.toUpperCase();
    }

    function hexToRgb(color) {
        const normalized = normalizeHexColor(color);
        if (!normalized) return null;
        return {
            red: Number.parseInt(normalized.slice(0, 2), 16),
            green: Number.parseInt(normalized.slice(2, 4), 16),
            blue: Number.parseInt(normalized.slice(4, 6), 16),
        };
    }

    function rgbToHex(red, green, blue) {
        return [red, green, blue]
            .map((channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, "0"))
            .join("")
            .toUpperCase();
    }

    function relativeLuminance({ red, green, blue }) {
        const values = [red, green, blue].map((channel) => {
            const normalized = channel / 255;
            return normalized <= 0.03928
                ? normalized / 12.92
                : ((normalized + 0.055) / 1.055) ** 2.4;
        });
        return (0.2126 * values[0]) + (0.7152 * values[1]) + (0.0722 * values[2]);
    }

    function mixColor(color, targetColor, amount) {
        const source = hexToRgb(color);
        const target = hexToRgb(targetColor);
        if (!source || !target) return color;

        return `#${rgbToHex(
            source.red + (target.red - source.red) * amount,
            source.green + (target.green - source.green) * amount,
            source.blue + (target.blue - source.blue) * amount,
        )}`;
    }

    function ensureCursorContrast(color) {
        const normalized = normalizeHexColor(color);
        if (!normalized) return color;

        const luminance = relativeLuminance(hexToRgb(normalized));
        const isDarkTheme = isDarkModeRef.value;
        const lowContrastThreshold = isDarkTheme ? 0.35 : 0.65;

        if (isDarkTheme && luminance < lowContrastThreshold) {
            return mixColor(`#${normalized}`, "#FFFFFF", 0.55);
        }

        if (!isDarkTheme && luminance > lowContrastThreshold) {
            return mixColor(`#${normalized}`, "#000000", 0.45);
        }

        return `#${normalized}`;
    }

    function resolveCursorColor(color) {
        return ensureCursorContrast(color);
    }

    function applyCursorThemeDefaults(force = false) {
        if (state.hasCustomCursorColors && !force) return;

        const defaults = getCursorThemeDefaults();
        state.isApplyingCursorThemeDefaults = true;
        state.normalCursorColor = defaults.normal;
        state.hoverCursorColor = defaults.hover;

        if (!state.isCursorPointer) state.cursorColor = resolveCursorColor(state.normalCursorColor);
        if (state.isCursorPointer) state.cursorColor = resolveCursorColor(state.hoverCursorColor);
        if (force) state.hasCustomCursorColors = false;
        state.isApplyingCursorThemeDefaults = false;
    }

    function handleCursorColorInput(colorType, event) {
        if (!state.isCursorActive) return;
        const colorValue = event?.target?.value;
        if (!colorValue) return;

        state[colorType] = colorValue;
        if (!state.isApplyingCursorThemeDefaults) state.hasCustomCursorColors = true;

        if (colorType === "normalCursorColor" && !state.isCursorPointer) {
            state.cursorColor = resolveCursorColor(state.normalCursorColor);
        }
        if (colorType === "hoverCursorColor" && state.isCursorPointer) {
            state.cursorColor = resolveCursorColor(state.hoverCursorColor);
        }
    }

    function handleCursor(e) {
        e.target.style.cursor = "none";
        e.target.addEventListener("mouseleave", (e) => { e.target.style.removeProperty("cursor"); }, { once: true });

        lastMouseRef.value = { x: e.clientX, y: e.clientY };

        if (cursorRef.value) {
            cursorRef.value.style.transform = `translate(${e.clientX}px,${e.clientY}px) scale(${state.cursorScale}%)`;
        }

        let hoverElementTag = e.target.tagName;
        if (["IMG", "SPAN", "svg", "path"].includes(hoverElementTag)) {
            hoverElementTag = e.target.parentElement.tagName;
        }

        if (["A", "BUTTON", "INPUT"].includes(hoverElementTag)) {
            state.cursorColor = resolveCursorColor(state.hoverCursorColor);
            state.isCursorPointer = true;
        } else {
            state.cursorColor = resolveCursorColor(state.normalCursorColor);
            state.isCursorPointer = false;
        }
    }

    function listenerHandleCursor(event) {
        handleCursor(event);
    }

    function toggleCursor() {
        state.isCursorActive = !state.isCursorActive;
    }

    function checkToggleCursor(e) {
        if (!state.isCursorActive) toggleCursor();
        const x = e?.clientX ?? lastMouseRef.value.x ?? window.innerWidth / 2;
        const y = e?.clientY ?? lastMouseRef.value.y ?? window.innerHeight / 2;
        if (cursorRef.value) {
            cursorRef.value.style.transform = `translate(${x}px,${y}px) scale(${state.cursorScale}%)`;
        }
    }

    function toggleCursorSettingsMenu() {
        state.isCursorSettingsMenuOpen = !state.isCursorSettingsMenuOpen;
        refreshSnapshot(380);
    }

    function changeScaleValue(e, type) {
        checkToggleCursor(e);
        if (type === "minus") state.cursorScale -= state.cursorScaleStep;
        if (type === "more") state.cursorScale += state.cursorScaleStep;
        if (state.isCursorActive && cursorRef.value) {
            const x = e?.clientX ?? lastMouseRef.value.x ?? window.innerWidth / 2;
            const y = e?.clientY ?? lastMouseRef.value.y ?? window.innerHeight / 2;
            cursorRef.value.style.transform = `translate(${x}px,${y}px) scale(${state.cursorScale}%)`;
        }
    }

    function changeCursorScale(type) {
        changeScaleValue(null, type);
    }

    function reset() {
        state.isCursorActive = false;
        state.isCursorSettingsMenuOpen = false;
        state.cursorScale = 100;
        state.isCursorPointer = false;
        applyCursorThemeDefaults(true);
    }

    function applyFromProfile(entry) {
        if (entry.isCursorActive) state.isCursorActive = entry.isCursorActive;
        if (entry.cursorColor) {
            state.normalCursorColor = entry.cursorColor;
            state.hasCustomCursorColors = true;
            state.cursorColor = resolveCursorColor(state.normalCursorColor);
        }
        if (entry.hoverCursorColor) {
            state.hoverCursorColor = entry.hoverCursorColor;
            state.hasCustomCursorColors = true;
            if (state.isCursorPointer) {
                state.cursorColor = resolveCursorColor(state.hoverCursorColor);
            }
        }
        if (entry.cursorScale) state.cursorScale = entry.cursorScale;
    }

    watch(
        () => state.isCursorActive,
        (active) => {
            if (active) {
                window.addEventListener("mousemove", listenerHandleCursor);
                if (cursorRef.value) cursorRef.value.style.display = "block";
                state.isCursorSettingsMenuOpen = true;
                const style = document.createElement("style");
                style.id = "tenjity-cursor-thumb";
                style.textContent = "input[type='range']::-webkit-slider-thumb { cursor: none !important; } input[type='range']::-moz-range-thumb { cursor: none !important; }";
                document.head.appendChild(style);
            } else {
                document.getElementById("tenjity-cursor-thumb")?.remove();
                state.isCursorSettingsMenuOpen = false;
                window.removeEventListener("mousemove", listenerHandleCursor);
                if (cursorRef.value) cursorRef.value.style.display = "none";
                const itemCursor = document.querySelector(".tenjityModule-item-cursor");
                if (itemCursor) {
                    itemCursor.style.removeProperty("cursor");
                    itemCursor.parentElement?.style.removeProperty("cursor");
                }
                const content = document.querySelector(".tenjityModule-content");
                if (content) content.style.removeProperty("cursor");
            }
        }
    );

    watch(
        isDarkModeRef,
        () => {
            if (state.isCursorPointer) {
                state.cursorColor = resolveCursorColor(state.hoverCursorColor);
            } else {
                state.cursorColor = resolveCursorColor(state.normalCursorColor);
            }
        }
    );

    return {
        state,
        getCursorThemeDefaults,
        applyCursorThemeDefaults,
        handleCursorColorInput,
        handleCursor,
        listenerHandleCursor,
        toggleCursor,
        checkToggleCursor,
        toggleCursorSettingsMenu,
        changeScaleValue,
        changeCursorScale,
        reset,
        applyFromProfile,
    };
}
